import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Badge } from '../ui/Badge';
import { cn } from '../../utils/cn';
import { useAuthStore } from '../../stores/authStore';

/**
 * Receivables collection (migration 151) — the owner's "MecaRadar Recouvrement" prototype.
 * Shared types and pieces for Recouvrement, Encaissements, Relances, Factures récapitulatives,
 * Comptes clients and Paramètres.
 */

export type Mode = 'TRANSFER' | 'LCR' | 'SEPA' | 'CHECK' | 'CARD' | 'CASH';
export type CustomerType = 'B2B_ACCOUNT' | 'B2B_PREPAID' | 'B2C';
export type InvoiceStatus =
  | 'REGLEE'
  | 'LITIGE'
  | 'EN_COURS'
  | 'A_ECHOIR'
  | 'PARTIELLE'
  | 'EN_RETARD'
  | 'PARTIELLE_RETARD';

export interface InvState {
  paid: number;
  pending: number;
  remaining: number;
  late: number;
  status: InvoiceStatus;
  bucket: string | null;
}
export interface Cust {
  id: string;
  name: string;
  code: string | null;
  type: CustomerType;
  mode: Mode | null;
  email: string | null;
  terms: string | null;
  sepa: { rum: string; signedAt: string | null } | null;
}
export interface NextStep {
  key: string | null;
  label: string;
  day: number | null;
  later: boolean;
}
export interface Inv {
  id: string;
  number: string;
  kind: 'RECAP' | 'INVOICE';
  date: string;
  dueDate: string;
  periodStart: string | null;
  gross: number;
  total: number;
  deliveryNotes: number;
  s: InvState;
  dispute: { id: string; reason: string; openedAt: string } | null;
  customer?: Cust;
  nextStep?: NextStep | null;
}
export interface Step {
  key: string;
  day: number;
  label: string;
  block: boolean;
  penalties: boolean;
  enabled: boolean;
}
export interface Exposure {
  undelivered: number;
  notInvoiced: number;
  invoicedUnpaid: number;
  inCollection: number;
  advance: number;
  total: number;
  limit: number | null;
  release: number;
  available: number | null;
  overdueAmount: number;
  maxLate: number;
  blocked: boolean;
  reasons: string[];
}
export interface Instrument {
  id: string;
  number: string;
  customerId: string;
  customerName: string;
  customerType: CustomerType;
  mode: Mode;
  amount: number;
  onAccount: number;
  date: string;
  dueDate: string | null;
  reference: string | null;
  state: string;
  remittance: string | null;
  rejectReason: string | null;
  createdBy: string | null;
  allocations: Array<{ invoiceId: string; number: string; amount: number }>;
  history: Array<{
    state: string;
    date: string;
    by: string | null;
    system: boolean;
    note: string | null;
  }>;
}

/** OWNER / ACCOUNTING run collection; the counter only records a private customer's card or cash. */
export function useCollectionRole() {
  const role = useAuthStore((s) => s.user?.role) as string | undefined;
  return {
    role,
    collector: role === 'OWNER' || role === 'ACCOUNTING' || role === 'ADMIN',
    owner: role === 'OWNER',
  };
}

export const cmd = (id?: string) => ({ headers: { 'X-Command-Id': id ?? crypto.randomUUID() } });

/** The server's sentence (collection messages are written in French by the rules). */
export function apiMessage(t: TFunction, err: unknown): string {
  const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
  return data?.message || t('collection.err.generic');
}

const STATUS_VARIANT: Record<
  InvoiceStatus,
  'success' | 'warning' | 'destructive' | 'secondary' | 'default' | 'muted'
> = {
  REGLEE: 'success',
  LITIGE: 'default',
  EN_COURS: 'default',
  A_ECHOIR: 'muted',
  PARTIELLE: 'warning',
  EN_RETARD: 'destructive',
  PARTIELLE_RETARD: 'destructive',
};
export function StatusPill({ s }: { s: InvState }) {
  const { t } = useTranslation();
  return (
    <Badge variant={STATUS_VARIANT[s.status]}>
      {t(`collection.status.${s.status}`, { days: s.late })}
    </Badge>
  );
}

const STATE_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'default' | 'muted'> = {
  RECU: 'success',
  ECHUE: 'success',
  PAYE: 'success',
  EMISE: 'warning',
  PROGRAMME: 'warning',
  REMISE: 'default',
  PRESENTE: 'default',
  REJETE: 'destructive',
};
export function StatePill({ state }: { state: string }) {
  const { t } = useTranslation();
  return (
    <Badge variant={STATE_VARIANT[state] ?? 'muted'}>
      {t(`collection.state.${state}`, { defaultValue: state })}
    </Badge>
  );
}

export const labelCls =
  'mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground';
export const thCls = 'px-3 py-2 text-left font-medium';
export const thNum = 'px-3 py-2 text-right font-medium';
export const tdCls = 'px-3 py-2 align-middle';
export const tdNum = 'px-3 py-2 text-right tabular-nums align-middle';

export function Tile({
  label,
  value,
  sub,
  alert,
  onClick,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  alert?: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border bg-card p-3 shadow-sm',
        onClick && 'cursor-pointer hover:border-primary/50'
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={cn('text-2xl font-bold tabular-nums', alert && 'text-destructive')}>
        {value}
      </div>
      {sub != null && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: Array<{ key: K; label: string; count?: number | null; alert?: boolean }>;
  value: K;
  onChange: (k: K) => void;
}) {
  return (
    <div className="flex gap-1 overflow-x-auto border-b px-3 pt-2" role="tablist">
      {tabs.map((x) => (
        <button
          key={x.key}
          type="button"
          role="tab"
          aria-selected={value === x.key}
          onClick={() => onChange(x.key)}
          className={cn(
            '-mb-px whitespace-nowrap border-b-[3px] px-3 py-2 text-sm',
            value === x.key
              ? 'border-primary font-semibold text-foreground'
              : 'border-transparent text-muted-foreground'
          )}
        >
          {x.label}
          {x.count != null && (
            <span
              className={cn(
                'ml-1.5 rounded-full px-1.5 text-xs',
                x.alert && x.count > 0
                  ? 'bg-destructive text-destructive-foreground'
                  : 'bg-muted text-muted-foreground'
              )}
            >
              {x.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export function Modal({
  title,
  lede,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  lede?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'flex max-h-[92vh] w-full flex-col overflow-hidden rounded-lg border bg-background shadow-xl',
          wide ? 'max-w-3xl' : 'max-w-xl'
        )}
      >
        <header className="border-b px-5 py-4">
          <h3 className="text-base font-semibold">{title}</h3>
          {lede && <p className="mt-1 text-sm text-muted-foreground">{lede}</p>}
        </header>
        <div className="grid gap-4 overflow-auto px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

export function Banner({ tone, children }: { tone: 'bad' | 'info' | 'ok'; children: ReactNode }) {
  return (
    <div
      className={cn(
        'mb-3 rounded-md px-3 py-2 text-sm font-medium',
        tone === 'bad' && 'bg-destructive/[.10] text-destructive',
        tone === 'info' && 'bg-primary-soft text-primary',
        tone === 'ok' && 'bg-success/[.12] text-success'
      )}
    >
      {children}
    </div>
  );
}

export const Empty = ({ children }: { children: ReactNode }) => (
  <div className="px-4 py-12 text-center text-sm text-muted-foreground">{children}</div>
);

export const monthLabel = (t: TFunction, m: string) => {
  const months = t('recap.months', { returnObjects: true }) as string[];
  const [y, mm] = m.split('-').map(Number);
  return `${months[mm - 1] ?? mm} ${y}`;
};
